// The bundle declares its own render path: scene.json `render_settings`. Absent means legacy.
// Notes: ~/comments/aos-threejs-poc/renderSettings_parseRenderSettings_bundle_selector.md

import { DEFAULT_LOG_LEVEL, asLogLevel, type LogLevel } from "./ogsLog.ts";

export type RenderPath = "legacy" | "next";

/** Where the resolved PATH came from. `error` means the bundle asked for something unreadable. */
export type RenderSettingsSource = "default" | "scene" | "error";

export interface RenderSettings {
  path: RenderPath;
  logging: LogLevel;
  source: RenderSettingsSource;
  /** Every refusal names itself; null on a clean parse. */
  error: string | null;
}

/** The scene.json key, and the one file the probe has to read to answer. */
export const RENDER_SETTINGS_KEY = "render_settings";
export const SCENE_FILE = "scene.json";

const PATHS: readonly RenderPath[] = ["legacy", "next"];

/** A bundle that declares nothing renders on the path it always did. */
export const LEGACY_SETTINGS: RenderSettings = Object.freeze({
  path: "legacy", logging: DEFAULT_LOG_LEVEL, source: "default", error: null,
});

function asPath(value: unknown): RenderPath | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  return (PATHS as readonly string[]).includes(key) ? (key as RenderPath) : null;
}

const quote = (value: unknown): string => (typeof value === "string" ? `"${value}"` : String(value));

/** `render_settings` read off a raw scene.json object. Never throws; a malformed value keeps legacy. */
export function parseRenderSettings(sceneJson: unknown): RenderSettings {
  const scene = sceneJson as Record<string, unknown> | null | undefined;
  if (!scene || typeof scene !== "object") return LEGACY_SETTINGS;
  const declared = scene[RENDER_SETTINGS_KEY];
  if (declared === undefined || declared === null) return LEGACY_SETTINGS;

  // Shorthand: the whole block is the path, for a bundle marked by hand.
  if (typeof declared === "string") {
    const path = asPath(declared);
    return path
      ? { path, logging: DEFAULT_LOG_LEVEL, source: "scene", error: null }
      : refuse(`${RENDER_SETTINGS_KEY} ${quote(declared)} is not "legacy" or "next"`);
  }
  if (typeof declared !== "object" || Array.isArray(declared)) {
    return refuse(`${RENDER_SETTINGS_KEY} must be a string or an object, not ${typeof declared}`);
  }

  const block = declared as Record<string, unknown>;
  // An object stating no path is a bundle configuring logging only — legacy, deliberately.
  const path = block.path === undefined || block.path === null ? "legacy" : asPath(block.path);
  if (!path) return refuse(`${RENDER_SETTINGS_KEY}.path ${quote(block.path)} is not "legacy" or "next"`);

  // A bad LOGGING value must not cost the bundle its renderer: the two fields are independent.
  const logging = block.logging === undefined || block.logging === null ? DEFAULT_LOG_LEVEL : asLogLevel(block.logging);
  if (!logging) {
    return {
      path, logging: DEFAULT_LOG_LEVEL, source: "scene",
      error: `${RENDER_SETTINGS_KEY}.logging ${quote(block.logging)} is not a level — using "${DEFAULT_LOG_LEVEL}"`,
    };
  }
  return { path, logging, source: "scene", error: null };
}

function refuse(error: string): RenderSettings {
  return { path: "legacy", logging: DEFAULT_LOG_LEVEL, source: "error", error };
}

/** One line naming the path, where it came from, and what was refused. */
export function describeRenderSettings(settings: RenderSettings): string {
  const where = settings.source === "scene" ? `declared by this bundle (${SCENE_FILE})`
    : settings.source === "error" ? `REFUSED — ${settings.error}`
      : `default (no ${RENDER_SETTINGS_KEY} in ${SCENE_FILE})`;
  return `render path '${settings.path}' — ${where}`;
}
